/*
 * LoginForm Component
 * 
 * Note: Browser console may show 400/429 HTTP errors from Supabase auth endpoints.
 * These are expected behaviors:
 * - 400 errors occur when attempting to sign in with unconfirmed email
 * - 429 errors occur when rate limits are hit on resend requests
 * The application handles these gracefully with user-friendly UI messages.
 */

import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, LogIn, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useRateLimiting } from '@/hooks/use-rate-limiting';
import { toast } from '@/hooks/use-toast';

interface LoginFormProps {
  onToggleMode: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onToggleMode }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const { signIn } = useAuth();
  const navigate = useNavigate();

  // Rate limiting for login attempts
  const { checkRateLimit, recordAction, lastResult: loginRateLimit } = useRateLimiting({
    action: 'auth:login',
    identifier: email,
    onRateLimitExceeded: (result) => {
      const retryAfter = result.retryAfter || 0;
      const minutes = Math.ceil(retryAfter / 60);
      toast({
        title: "Too Many Login Attempts",
        description: `Please wait ${minutes} minutes before trying again.`,
        variant: "destructive",
      });
    }
  });

  // Rate limiting for email resend
  const { checkRateLimit: checkResendLimit, recordAction: recordResendAction } = useRateLimiting({
    action: 'auth:email_verification',
    identifier: email,
    onRateLimitExceeded: (result) => {
      const retryAfter = result.retryAfter || 0;
      const minutes = Math.ceil(retryAfter / 60);
      toast({
        title: "Too Many Email Requests",
        description: `Please wait ${minutes} minutes before requesting another email.`,
        variant: "destructive",
      });
    }
  });

  // Development Note: Network errors (400/429) that appear in browser console 
  // are expected behavior from Supabase for unconfirmed emails and rate limiting

  // Countdown timer for resend cooldown
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setShowEmailConfirmation(false);

    try {
      // Check rate limit before attempting login
      const rateLimitResult = await checkRateLimit();
      if (!rateLimitResult.allowed) {
        setLoading(false);
        return;
      }

      // Note: A 400 network error may appear in console for unconfirmed emails - this is expected
      const { error } = await signIn(email, password);

      if (error) {
        // Record failed login attempt
        await recordAction();
        
        // Check if it's an email confirmation error
        if (error.message?.includes('email not confirmed') || 
            error.message?.includes('Email not confirmed') ||
            error.message?.includes('signup_not_verified') ||
            error.message?.includes('Email link is invalid') ||
            error.message?.includes('confirm your email')) {
          // Don't log this as it's expected behavior
          // The 400 error in network console is normal for unconfirmed emails
          setShowEmailConfirmation(true);
          setError('');
        } else {
          setError(error.message || 'Login failed');
        }
      } else {
        // Successful login - reset rate limit
        await recordAction();
      }
    } catch (err) {
      await recordAction();
      setError('An unexpected error occurred. Please try again.');
    }

    setLoading(false);
  };

  const handleResendConfirmation = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    setError('');

    try {
      // Check rate limit before resending
      const rateLimitResult = await checkResendLimit();
      if (!rateLimitResult.allowed) {
        setResendLoading(false);
        return;
      }

      // Note: A 429 network error may appear in console for rate limiting - this is expected
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email
      });

      if (error) {
        // Handle specific error types gracefully
        if (error.message?.includes('rate limit') || 
            error.message?.includes('too many requests') ||
            error.message?.includes('429')) {
          // The 429 error in network console is normal for rate limiting
          setError('Please wait before requesting another email confirmation.');
          setResendCooldown(60); // 60 second cooldown
        } else {
          setError(error.message || 'Failed to resend confirmation email');
        }
      } else {
        setResendSuccess(true);
        setResendCooldown(60); // 60 second cooldown
        await recordResendAction();
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    }

    setResendLoading(false);
  };

  if (showEmailConfirmation) {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center text-amber-600">
            Please Confirm Your Email
          </CardTitle>
          <CardDescription className="text-center">
            You need to confirm your email address before you can sign in
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {resendSuccess && (
            <Alert>
              <AlertDescription>
                Confirmation email sent! Please check your inbox and spam folder.
              </AlertDescription>
            </Alert>
          )}

          <div className="text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              We sent a confirmation link to: <br />
              <strong>{email}</strong>
            </p>
            
            <Button
              onClick={handleResendConfirmation}
              disabled={resendLoading || resendCooldown > 0}
              className="w-full"
              variant="outline"
            >
              {resendLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : resendCooldown > 0 ? (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Resend in {resendCooldown}s
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Resend Confirmation Email
                </>
              )}
            </Button>

            <Button
              onClick={() => {
                setShowEmailConfirmation(false);
                setError('');
                setResendSuccess(false);
                setResendCooldown(0);
              }}
              variant="ghost"
              className="w-full"
            >
              Back to Sign In
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold text-center">Welcome Back</CardTitle>
        <CardDescription className="text-center">
          Sign in to your WattFactory account
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
            <div className="flex justify-end">
              <Button
                type="button"
                variant="link"
                className="text-sm text-muted-foreground hover:text-foreground p-0 h-auto"
                onClick={() => navigate('/reset-password')}
              >
                Forgot Password?
              </Button>
            </div>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={loading || !email || !password}
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Signing in...
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4 mr-2" />
                Sign In
              </>
            )}
          </Button>
          
          <div className="text-center mt-4">
            <p className="text-sm text-muted-foreground">
              New to WattFactory?{' '}
              <a 
                href="/" 
                className="text-primary hover:underline"
              >
                Request beta access
              </a>
            </p>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}; 